'use client';

import clsx from 'clsx';
import Link from 'next/link';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { api, type RouterInputs, skipBatchOption } from '~/utils/api';
import { serverError } from '~/utils/serverError';

import { Ban, ChevronDown } from 'lucide-react';
import { ActionType, Permission, PolicyFeedbackAnnotationScope } from '@prisma/client';

import Button from '../Button';
import Quote from '../Quote';
import Textarea from '../Textarea';
import Tooltip from '../Tooltip';
import { type PolicyCardProps } from './PolicyCard';
import { type FeedbackListExitHandlers } from './FeedbackList';
import { toast } from '~/utils/toast';
import { usePopupManager } from '~/hooks/usePopupManager';
import {
  FeedbackFieldLabel,
  FeedbackTeachPanel,
  sharedFeedbackText,
  teachPanelControlClassName,
} from './FeedbackItemFrame';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { policyEnginePath } from '~/utils/policyEnginePath';
import { cn } from '~/lib/utils';
import useAbility from '~/hooks/useAbility';
import { localStorageKeys, useLocalStorage } from '~/hooks/useLocalStorage';

type IgnoreFeedbackInput = RouterInputs['feedback']['ignoreFeedback'];

const CHOOSABLE_IGNORE_SCOPES = new Set<PolicyFeedbackAnnotationScope>([
  PolicyFeedbackAnnotationScope.WRONG_POLICY,
  PolicyFeedbackAnnotationScope.FALSE_POSITIVE,
  PolicyFeedbackAnnotationScope.IGNORE_PHRASING_FOR_BRAND,
  PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_BRAND,
  PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_PAGE,
]);

const BRAND_IGNORE_SCOPES = new Set<PolicyFeedbackAnnotationScope>([
  PolicyFeedbackAnnotationScope.IGNORE_PHRASING_FOR_BRAND,
  PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_BRAND,
]);

function readLastIgnoreScope(): PolicyFeedbackAnnotationScope | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(localStorageKeys.ignoreFeedbackScope);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'string' &&
      CHOOSABLE_IGNORE_SCOPES.has(parsed as PolicyFeedbackAnnotationScope)
    ) {
      return parsed as PolicyFeedbackAnnotationScope;
    }
    return null;
  } catch {
    return null;
  }
}

/** Last submitted scope, otherwise the brand phrasing reviewers use most. */
function resolveDefaultIgnoreScope({
  lastUsed,
  hasBrand,
  limitToPage,
}: {
  lastUsed: PolicyFeedbackAnnotationScope | null;
  hasBrand: boolean;
  limitToPage: boolean;
}): PolicyFeedbackAnnotationScope {
  if (limitToPage) return PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_PAGE;
  if (lastUsed && CHOOSABLE_IGNORE_SCOPES.has(lastUsed)) {
    if (!BRAND_IGNORE_SCOPES.has(lastUsed) || hasBrand) return lastUsed;
  }
  if (hasBrand) return PolicyFeedbackAnnotationScope.IGNORE_PHRASING_FOR_BRAND;
  return PolicyFeedbackAnnotationScope.FALSE_POSITIVE;
}

// ====================

type DropdownProps<T extends string> = {
  options: Array<{ value: T; label: string; reasonForDisable?: string }>;
  onChange: (value: T) => void;
  value: string | undefined | null;
  placeholder?: string;
};

const Dropdown = <T extends string>(props: DropdownProps<T>) => {
  const { value, onChange, options } = props;
  const ref = useRef<HTMLDetailsElement | null>(null);

  const closeDropdown = () => {
    ref.current?.removeAttribute('open');
  };

  const onClickOutside = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      closeDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <details className="relative ml-0!" ref={ref}>
      <summary className="flex cursor-pointer items-center gap-1" role="button">
        <div
          className={clsx(
            'min-w-max text-[13px] font-medium tracking-tight',
            !selected && 'text-muted-foreground italic',
          )}
        >
          {selected?.label || props.placeholder}
        </div>
        <ChevronDown className="text-muted-foreground size-4" strokeWidth={2} />
      </summary>
      <ul className="border-border bg-background absolute z-10 mt-1 max-h-[220px] space-y-0.5 overflow-y-auto rounded-lg border p-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.07)]">
        {options.map((o) => (
          <li
            key={o.value}
            className={clsx(
              'hover:bg-accent min-w-max rounded-md px-2 py-1 text-[13px]',
              o.reasonForDisable && 'text-muted-foreground italic',
            )}
            title={o.reasonForDisable}
            onClick={(e) => {
              if (o.reasonForDisable) return;
              e.stopPropagation();
              onChange(o.value);
              closeDropdown();
            }}
          >
            <span className={o.reasonForDisable ? 'cursor-not-allowed' : undefined}>{o.label}</span>
          </li>
        ))}
      </ul>
    </details>
  );
};

type ScopeCard = {
  scope: PolicyFeedbackAnnotationScope;
  title: string;
  description: string;
};

type IgnoreScopeProps = {
  input: IgnoreFeedbackInput;
  setInput: Dispatch<SetStateAction<IgnoreFeedbackInput>>;
  shouldLimitScope: boolean;
} & Pick<
  IgnoreFeedbackProps,
  'feedbackId' | 'text' | 'brand' | 'documentId' | 'type' | 'evaluationType' | 'policy'
>;

const IgnoreScope = (props: IgnoreScopeProps) => {
  const { input, setInput } = props;
  const brandName = props.brand?.name;

  const cards: ScopeCard[] = props.shouldLimitScope
    ? [
        {
          scope: PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_PAGE,
          title: 'Ignore this policy on this page',
          description: 'Skip this check for this document only. Fine-tuning is not available here.',
        },
      ]
    : [
        {
          scope: PolicyFeedbackAnnotationScope.WRONG_POLICY,
          title: 'Wrong policy',
          description: 'A different policy should have caught this.',
        },
        {
          scope: PolicyFeedbackAnnotationScope.FALSE_POSITIVE,
          title: 'Ignore this phrasing everywhere',
          description: 'This wording is fine across all reviews.',
        },
        {
          scope: PolicyFeedbackAnnotationScope.IGNORE_PHRASING_FOR_BRAND,
          title: brandName
            ? `Ignore this phrasing for ${brandName}`
            : 'Ignore this phrasing for this brand',
          description: 'This wording is fine for this brand, but keep checking it elsewhere.',
        },
        {
          scope: PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_BRAND,
          title: brandName
            ? `Ignore this policy for ${brandName}`
            : 'Ignore this policy for this brand',
          description: 'Do not apply this policy to this brand.',
        },
        {
          scope: PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_PAGE,
          title: 'Ignore this policy on this page',
          description: 'Skip this check for this document only.',
        },
      ];

  const allPolicies =
    props.evaluationType?.documentEvaluationTypePolicyGroup
      .map((i) => i.policyGroup.policies)
      .flat()
      .flat()
      .sort((a, b) => a.name.localeCompare(b.name)) || [];

  const isWrongPolicy = input.scope === PolicyFeedbackAnnotationScope.WRONG_POLICY;

  return (
    <div className="flex flex-col gap-2">
      {cards.map((card) => {
        const selected = input.scope === card.scope;
        const brandRequired =
          card.scope === PolicyFeedbackAnnotationScope.IGNORE_PHRASING_FOR_BRAND ||
          card.scope === PolicyFeedbackAnnotationScope.IGNORE_POLICY_FOR_BRAND;
        const disabled = brandRequired && !props.brand;
        return (
          <button
            key={card.scope}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setInput((prev) => ({
                ...prev,
                scope: card.scope,
                correctPolicyId:
                  card.scope === PolicyFeedbackAnnotationScope.WRONG_POLICY
                    ? prev.correctPolicyId
                    : null,
              }));
            }}
            className={cn(
              'rounded-lg border px-3 py-2.5 text-left transition-colors',
              selected ? 'border-foreground/30 bg-muted/60' : 'border-border hover:bg-muted/40',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <div className="text-foreground text-[13px] font-medium tracking-tight">
              {card.title}
            </div>
            <p className="text-muted-foreground mt-0.5 text-[13px] leading-5">{card.description}</p>
            {disabled ? (
              <p className="text-muted-foreground mt-1 text-[13px]">
                This document is not associated with a brand.
              </p>
            ) : null}
            {selected &&
            isWrongPolicy &&
            card.scope === PolicyFeedbackAnnotationScope.WRONG_POLICY ? (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <FeedbackFieldLabel>Which policy should have caught this?</FeedbackFieldLabel>
                <Dropdown
                  options={allPolicies
                    .filter((p) => p.id !== props.policy?.id)
                    .map((p) => ({
                      label: p.name,
                      value: p.id,
                    }))}
                  onChange={(value) => {
                    setInput((prev) => ({
                      ...prev,
                      scope: PolicyFeedbackAnnotationScope.WRONG_POLICY,
                      correctPolicyId: value || null,
                    }));
                  }}
                  value={input.correctPolicyId}
                  placeholder="Select a policy"
                />
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

const noSettledIds: string[] = [];

export type IgnoreFeedbackProps = Pick<
  PolicyCardProps,
  | 'policy'
  | 'text'
  | 'reloadDoc'
  | 'brand'
  | 'documentId'
  | 'concatenateFeedbacks'
  | 'onRemove'
  | 'evaluationType'
> & {
  feedbackId: string;
  type: 'claim' | 'policyFeedback';
  /** The FeedbackList row id — exit animation runs only when the removed id matches this. */
  listRowId?: string;
  disabled?: boolean;
  /** Findings in a grouped card that already have a thumbs-up or ignore result. */
  settledIds?: string[];
  /** Keep this finding on the card and report which item was ignored. */
  leaveFindingOpen?: boolean;
  onAffected?: (feedbackId: string) => void;
} & Partial<FeedbackListExitHandlers>;

export default function IgnoreFeedback(props: IgnoreFeedbackProps) {
  const {
    policy,
    feedbackId,
    text,
    reloadDoc,
    brand,
    documentId,
    evaluationType,
    concatenateFeedbacks,
    onRemove,
    beginExit,
    restoreExit,
    listRowId,
    type,
    disabled = false,
    settledIds = noSettledIds,
    leaveFindingOpen = false,
    onAffected,
  } = props;
  const { workspaceId } = useWorkspace();
  const ability = useAbility();
  const canViewTeachQueue = !!ability?.can('do', Permission.VIEW_POLICY_ENGINE);
  const openFeedbacks = concatenateFeedbacks.filter((item) => !settledIds.includes(item.id));
  const needsSelection = openFeedbacks.length > 1;
  const [step, setStep] = useState<1 | 2 | 3>(needsSelection ? 1 : 2);
  const [explanation, setExplanation] = useState<string>('');
  const utils = api.useUtils();
  const { mutateAsync: ignoreFeedback } = api.feedback.ignoreFeedback.useMutation();
  const [isIgnoring, setIsIgnoring] = useState(false);
  const [, setLastIgnoreScope] = useLocalStorage(localStorageKeys.ignoreFeedbackScope, null);

  // Use unique ID for this popup instance
  const popupId = `ignore-feedback-${feedbackId}`;
  const { isOpen, openPopup, closePopup } = usePopupManager(popupId);
  const { data: allPolicies } = api.policyEngine.getAllPolicies.useQuery(
    {
      policyIds: concatenateFeedbacks.length
        ? concatenateFeedbacks.map((i) => i.policyId)
        : policy
          ? [policy.id]
          : [],
    },
    { ...skipBatchOption },
  );

  const baseInput: Pick<IgnoreFeedbackInput, 'markedClaimId' | 'markedPolicyFeedbackId'> =
    type === 'claim'
      ? { markedClaimId: feedbackId, markedPolicyFeedbackId: null }
      : { markedPolicyFeedbackId: feedbackId, markedClaimId: null };
  const [input, setInput] = useState<IgnoreFeedbackInput>({
    ...baseInput,
    scope: resolveDefaultIgnoreScope({
      lastUsed: readLastIgnoreScope(),
      hasBrand: !!brand,
      limitToPage: false,
    }),
    correctPolicyId: null,
    explanation: '',
    rejected: true,
  });

  const fullPolicy = allPolicies?.find((p) => {
    let policyId = policy?.id;
    if (input.markedPolicyFeedbackId) {
      const pf = concatenateFeedbacks.find((f) => f.id === input.markedPolicyFeedbackId);
      policyId = pf?.policyId;
    }
    return p.id === policyId;
  });
  const shouldLimitScope = !!fullPolicy?.steps.some(
    (s) =>
      s.visionPrompt ||
      s.nextStepActionType === ActionType.FAILURE ||
      s.precedingStepActionType === ActionType.FAILURE,
  );

  useEffect(() => {
    if (shouldLimitScope) {
      setInput((prev) => {
        return {
          ...prev,
          scope: 'IGNORE_POLICY_FOR_PAGE',
        };
      });
    }
  }, [shouldLimitScope, setInput]);

  const wasOpenRef = useRef(false);
  useLayoutEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setInput((prev) => ({
        ...prev,
        scope: resolveDefaultIgnoreScope({
          lastUsed: readLastIgnoreScope(),
          hasBrand: !!brand,
          limitToPage: shouldLimitScope,
        }),
        correctPolicyId: null,
      }));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, brand, shouldLimitScope]);

  useEffect(() => {
    // Reset the marked feedback after the modal is closed
    if (!isOpen) {
      const markedFeedbackId = openFeedbacks[0]?.id || concatenateFeedbacks[0]?.id || feedbackId;
      setInput((prev) => ({
        ...prev,
        ...(type === 'claim'
          ? { markedClaimId: markedFeedbackId, markedPolicyFeedbackId: null }
          : { markedPolicyFeedbackId: markedFeedbackId, markedClaimId: null }),
      }));
      setStep(openFeedbacks.length > 1 ? 1 : 2);
      setExplanation('');
    }
  }, [isOpen, concatenateFeedbacks, feedbackId, settledIds]);

  const viewStep: 1 | 2 | 3 = !needsSelection && step === 1 ? 2 : step;
  const sharedSelectionText = sharedFeedbackText(concatenateFeedbacks);

  if (!policy) return <></>;

  return (
    <>
      <Tooltip content={disabled ? 'Already reviewed' : 'Ignore this finding'} hideIcon>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={disabled ? 'Already reviewed' : 'Ignore this finding'}
          disabled={disabled}
          className={disabled ? 'opacity-40 transition-none' : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            openPopup();
          }}
        >
          <Ban className="size-4" strokeWidth={2} />
        </Button>
      </Tooltip>
      {isOpen && (
        <FeedbackTeachPanel
          title="Teach Your AI"
          description={
            viewStep === 1
              ? 'This finding should have been ignored.'
              : viewStep === 2
                ? 'Choose how broadly this teaching applies.'
                : 'Explain why so the model can learn.'
          }
          onClose={closePopup}
          className="max-w-[600px]"
          footer={
            viewStep === 1 ? (
              <Button
                type="button"
                label="Continue"
                size="sm"
                className="w-full"
                onClick={() => setStep(2)}
              />
            ) : viewStep === 2 ? (
              <>
                {needsSelection ? (
                  <Button
                    type="button"
                    label="Back"
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(1)}
                  />
                ) : null}
                <Button
                  type="button"
                  label="Continue"
                  size="sm"
                  className={needsSelection ? 'flex-1' : 'w-full'}
                  disabled={
                    input.scope === PolicyFeedbackAnnotationScope.WRONG_POLICY &&
                    !input.correctPolicyId
                  }
                  onClick={() => {
                    if (
                      input.scope === PolicyFeedbackAnnotationScope.WRONG_POLICY &&
                      !input.correctPolicyId
                    ) {
                      toast.error('Please select a correct policy');
                      return;
                    }
                    setStep(3);
                  }}
                />
              </>
            ) : (
              <>
                <Button
                  type="button"
                  label="Back"
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(2)}
                />
                <Button
                  type="button"
                  label="Submit"
                  size="sm"
                  className="flex-1"
                  isLoading={isIgnoring}
                  onClick={async () => {
                    const removedId =
                      input.markedClaimId || input.markedPolicyFeedbackId || undefined;
                    const rowId = listRowId ?? removedId;
                    const shouldAnimateExit =
                      !leaveFindingOpen &&
                      !!removedId &&
                      !!beginExit &&
                      !!restoreExit &&
                      removedId === rowId;

                    const mutation = ignoreFeedback({
                      ...input,
                      correctPolicyId:
                        input.scope === PolicyFeedbackAnnotationScope.WRONG_POLICY
                          ? input.correctPolicyId
                          : null,
                      explanation,
                      leaveFindingOpen,
                    });

                    if (shouldAnimateExit) {
                      beginExit(rowId, 'remove', mutation);
                    }

                    try {
                      setIsIgnoring(true);
                      await mutation;

                      if (!shouldLimitScope) {
                        setLastIgnoreScope(input.scope);
                      }

                      if (leaveFindingOpen && removedId) {
                        onAffected?.(removedId);
                        void reloadDoc();
                      } else if (removedId && !shouldAnimateExit) {
                        onRemove?.(removedId);
                        reloadDoc();
                      }
                      void utils.dataStore.getAllAnnotations.invalidate(
                        documentId ? { documentId } : undefined,
                      );
                      toast.teachingPending({
                        teachYourAiHref: canViewTeachQueue
                          ? policyEnginePath(workspaceId, '/policy-annotations')
                          : null,
                      });
                      closePopup();
                    } catch (e) {
                      if (shouldAnimateExit) {
                        restoreExit(rowId);
                      }
                      serverError(e as Error);
                    } finally {
                      setIsIgnoring(false);
                    }
                  }}
                />
              </>
            )
          }
        >
          <p className="text-muted-foreground mb-3 text-[13px]">
            Step {needsSelection ? viewStep : viewStep === 3 ? 2 : 1} of {needsSelection ? 3 : 2}
          </p>
          {viewStep === 1 && needsSelection ? (
            <div>
              {sharedSelectionText ? (
                <Quote
                  text={sharedSelectionText}
                  className="mb-2 line-clamp-4 py-1.5 text-[13px]"
                  title={sharedSelectionText}
                />
              ) : null}
              {openFeedbacks.map((i) => (
                <div key={i.id}>
                  <label className="block cursor-pointer py-1">
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="select-feedback"
                        checked={
                          type === 'claim'
                            ? i.id === input.markedClaimId
                            : i.id === input.markedPolicyFeedbackId
                        }
                        className="border-input size-3.5 shrink-0 accent-foreground leading-none"
                        onChange={(e) =>
                          e.target.checked &&
                          setInput((prev) => ({
                            ...prev,
                            [type === 'claim' ? 'markedClaimId' : 'markedPolicyFeedbackId']: i.id,
                          }))
                        }
                      />
                      <Link
                        className="text-foreground text-[13px] leading-4 font-medium tracking-tight underline decoration-foreground/35 underline-offset-[3px] hover:decoration-foreground"
                        href={policyEnginePath(
                          workspaceId,
                          `/group/${i.policy.policyGroupId}/policy/${i.policy.id}`,
                        )}
                        target="_blank"
                      >
                        {i.policy.name}
                      </Link>
                    </span>
                    {!sharedSelectionText && i.text ? (
                      <Quote
                        text={i.text}
                        className="mt-1.5 ml-[1.375rem] line-clamp-4 py-1.5 text-[13px]"
                        title={i.text}
                      />
                    ) : null}
                  </label>
                </div>
              ))}
            </div>
          ) : null}
          {viewStep === 2 ? (
            <>
              {!needsSelection ? (
                <div className="mb-4 space-y-1.5">
                  <Link
                    className="text-foreground text-[13px] font-medium tracking-tight underline decoration-foreground/35 underline-offset-[3px] hover:decoration-foreground"
                    href={policyEnginePath(
                      workspaceId,
                      `/group/${policy.policyGroupId}/policy/${policy.id}`,
                    )}
                    target="_blank"
                  >
                    {policy.name}
                  </Link>
                  {text ? <Quote text={text} className="py-1.5 text-[13px]" /> : null}
                </div>
              ) : null}
              <IgnoreScope
                feedbackId={feedbackId}
                input={input}
                setInput={setInput}
                brand={brand}
                documentId={documentId}
                text={text}
                type={type}
                evaluationType={evaluationType}
                policy={{
                  ...(concatenateFeedbacks.find(
                    (i) =>
                      (type === 'policyFeedback' && i.id === input.markedPolicyFeedbackId) ||
                      (type === 'claim' && i.id === input.markedClaimId),
                  )?.policy || policy),
                  hasVisionPrompt: false,
                }}
                shouldLimitScope={shouldLimitScope}
              />
            </>
          ) : null}
          {viewStep === 3 ? (
            <>
              <FeedbackFieldLabel>Why was it wrong?</FeedbackFieldLabel>
              <Textarea
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                textareaProps={{ minRows: 2, className: teachPanelControlClassName }}
              />
            </>
          ) : null}
        </FeedbackTeachPanel>
      )}
    </>
  );
}
